import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CorpHeader } from "@/components/CorpHeader";
import { API, adminApi, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Save, RotateCcw, ExternalLink, History, Undo2, X } from "lucide-react";

/** Grouped list of every i18n key the public landing page renders. Admin
 *  can override any of these; blanking a field reverts to the default. */
function formatHistoryDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function countChanges(entry) {
  const en = Object.keys(entry.snapshot_en || {}).length;
  const th = Object.keys(entry.snapshot_th || {}).length;
  return en + th;
}

const SECTIONS = [
  { title: "Header & footer", fields: [
    { key: "public.admin_portal", label: "Admin portal button" },
  ]},
  { title: "Hero", fields: [
    { key: "public.hero.eyebrow", label: "Eyebrow label" },
    { key: "public.hero.title_a", label: "Headline line 1" },
    { key: "public.hero.title_b", label: "Headline line 2 (highlighted)" },
    { key: "public.hero.subtitle", label: "Subtitle paragraph", long: true },
    { key: "public.hero.cta_contact", label: "Primary CTA button" },
    { key: "public.hero.cta_benefits", label: "Secondary CTA button" },
    { key: "public.hero.pill_bilingual", label: "Pill — bilingual" },
    { key: "public.hero.pill_multi_tenant", label: "Pill — multi-shop" },
    { key: "public.hero.pill_reports", label: "Pill — reports" },
    { key: "public.sample.heading", label: "Sample card — heading" },
    { key: "public.sample.subtitle", label: "Sample card — subtitle" },
    { key: "public.sample.customers", label: "Sample card — customers label" },
    { key: "public.sample.footer", label: "Sample card — footer" },
  ]},
  { title: "Benefits", fields: [
    { key: "public.benefits.title", label: "Section title" },
    { key: "public.benefits.subtitle", label: "Section subtitle", long: true },
    { key: "public.benefit.multi.title", label: "Card 1 title" },
    { key: "public.benefit.multi.body", label: "Card 1 body", long: true },
    { key: "public.benefit.reports.title", label: "Card 2 title" },
    { key: "public.benefit.reports.body", label: "Card 2 body", long: true },
    { key: "public.benefit.staff.title", label: "Card 3 title" },
    { key: "public.benefit.staff.body", label: "Card 3 body", long: true },
  ]},
  { title: "Pricing banner", fields: [
    { key: "public.pricing.banner", label: "Banner headline", long: true },
  ]},
  { title: "Agents wanted", fields: [
    { key: "public.agents.eyebrow", label: "Eyebrow" },
    { key: "public.agents.title", label: "Title" },
    { key: "public.agents.body", label: "Body paragraph", long: true },
    { key: "public.agents.point_1", label: "Bullet 1" },
    { key: "public.agents.point_2", label: "Bullet 2" },
    { key: "public.agents.point_3", label: "Bullet 3" },
    { key: "public.agents.contact_label", label: "Contact label" },
    { key: "public.agents.cta", label: "CTA button" },
  ]},
];

export default function AdminLandingEdit() {
  const nav = useNavigate();
  const { t } = useLang();
  const [en, setEn] = useState({});
  const [th, setTh] = useState({});
  const [initialEn, setInitialEn] = useState({});
  const [initialTh, setInitialTh] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [reverting, setReverting] = useState(null);

  const loadHistory = async () => {
    try {
      const r = await adminApi.get("/admin/landing-content/history");
      setHistory(r.data?.entries || []);
    } catch { /* history is best-effort — don't block the editor */ }
  };

  // Flat list of all editable keys, for defaults lookup.
  const allKeys = useMemo(
    () => SECTIONS.flatMap(s => s.fields.map(f => f.key)),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/landing-content`);
        setEn(r.data?.en || {}); setInitialEn(r.data?.en || {});
        setTh(r.data?.th || {}); setInitialTh(r.data?.th || {});
      } catch (e) {
        toast.error("Failed to load landing content");
      } finally { setLoading(false); }
    })();
    loadHistory();
  }, []);

  const setField = (lang, key, value) => {
    if (lang === "en") setEn(prev => ({ ...prev, [key]: value }));
    else setTh(prev => ({ ...prev, [key]: value }));
  };

  // Live default string for the current running i18n dictionary. Rendered as
  // a muted "defaults to:" hint under empty fields so the admin always sees
  // what the public page will show when the override is cleared.
  const defaultFor = (lang, key) => {
    // Call the i18n t() in whichever language we need. We memoised t()
    // on the lang in context, so we locally pull the raw dict too.
    try {
      // Lazy-import to avoid circulars.
      const DICT = require("@/lib/i18n").__DICT;
      return (DICT && DICT[lang] && DICT[lang][key]) || "";
    } catch { return ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminApi.put("/admin/landing-content", { en, th });
      setInitialEn(r.data.en || {}); setInitialTh(r.data.th || {});
      toast.success("Landing page saved");
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally { setSaving(false); }
  };

  const revert = async (entry) => {
    if (!window.confirm(
      `Revert to the version saved on ${formatHistoryDate(entry.replaced_at)}?\n\nYour current content will be moved into history and can be restored.`
    )) return;
    setReverting(entry.id);
    try {
      const r = await adminApi.post(`/admin/landing-content/revert/${entry.id}`);
      setEn(r.data.en || {}); setInitialEn(r.data.en || {});
      setTh(r.data.th || {}); setInitialTh(r.data.th || {});
      toast.success("Reverted to earlier version");
      loadHistory();
      setShowHistory(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Revert failed");
    } finally { setReverting(null); }
  };

  const resetChanges = () => {
    setEn({ ...initialEn }); setTh({ ...initialTh });
    toast("Unsaved changes discarded");
  };

  const dirty = useMemo(() => {
    const diff = (a, b) => {
      const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
      for (const k of keys) if ((a[k] || "") !== (b[k] || "")) return true;
      return false;
    };
    return diff(en, initialEn) || diff(th, initialTh);
  }, [en, th, initialEn, initialTh]);

  const renderField = (field) => {
    const Comp = field.long ? Textarea : Input;
    return (
      <div key={field.key} className="grid md:grid-cols-2 gap-3 py-3 border-b border-stone-100 last:border-none">
        <div className="md:col-span-2 flex items-start justify-between gap-3">
          <div>
            <label className="text-sm font-medium text-stone-800">{field.label}</label>
            <code className="block mt-0.5 text-[11px] text-stone-400 font-mono">{field.key}</code>
          </div>
        </div>
        <div>
          <div className="caption-label text-stone-500 mb-1">English</div>
          <Comp
            value={en[field.key] || ""}
            onChange={(e) => setField("en", field.key, e.target.value)}
            rows={field.long ? 3 : undefined}
            placeholder={defaultFor("en", field.key) || "—"}
            data-testid={`landing-edit-en-${field.key}`}
          />
        </div>
        <div>
          <div className="caption-label text-stone-500 mb-1">ไทย</div>
          <Comp
            value={th[field.key] || ""}
            onChange={(e) => setField("th", field.key, e.target.value)}
            rows={field.long ? 3 : undefined}
            placeholder={defaultFor("th", field.key) || "—"}
            data-testid={`landing-edit-th-${field.key}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <CorpHeader />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 -ml-2"
              onClick={() => nav("/admin")}
              data-testid="landing-edit-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to dashboard
            </Button>
            <h1 className="font-serif text-4xl text-stone-900">Edit landing page</h1>
            <p className="text-sm text-stone-500 mt-1">
              Override any text on <span className="font-mono">/</span> in English and Thai. Blank fields fall back to the built-in copy.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="landing-edit-preview"
            >
              <a href="/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setShowHistory(v => !v)}
              data-testid="landing-edit-history-toggle"
            >
              <History className="w-3.5 h-3.5 mr-1" /> History {history.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-stone-200 text-[11px] font-mono text-stone-700">{history.length}</span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={resetChanges}
              disabled={!dirty || saving}
              data-testid="landing-edit-reset"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
            <Button
              size="sm"
              className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-amber-50"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="landing-edit-save"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center text-stone-500">Loading…</div>
        ) : (
          <>
            {showHistory && (
              <Card className="border-stone-200/80 mb-6" data-testid="landing-history-panel">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h2 className="font-serif text-xl text-stone-900 flex items-center gap-2">
                        <History className="w-4 h-4 text-emerald-800" /> Change history
                      </h2>
                      <p className="text-sm text-stone-500 mt-0.5">Last 10 saves — click Revert to restore any earlier version in one click.</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowHistory(false)}
                      className="rounded-full"
                      data-testid="landing-history-close"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {history.length === 0 ? (
                    <div className="py-6 text-center text-sm text-stone-500">
                      No history yet — your first save will appear here next time.
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100">
                      {history.map((entry, idx) => (
                        <div key={entry.id} className="py-3 flex items-center justify-between gap-3" data-testid={`history-row-${idx}`}>
                          <div className="min-w-0">
                            <div className="text-sm text-stone-900">
                              {idx === 0 ? "Replaced by latest save" : `Replaced on ${formatHistoryDate(entry.replaced_at)}`}
                            </div>
                            <div className="text-xs text-stone-500 font-mono mt-0.5">
                              by {entry.replaced_by || "—"} · {countChanges(entry)} override{countChanges(entry) === 1 ? "" : "s"}
                              {entry.originally_saved_at && (
                                <> · originally saved {formatHistoryDate(entry.originally_saved_at)}</>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => revert(entry)}
                            disabled={reverting === entry.id}
                            className="rounded-full shrink-0"
                            data-testid={`history-revert-${idx}`}
                          >
                            <Undo2 className="w-3.5 h-3.5 mr-1" />
                            {reverting === entry.id ? "Reverting…" : "Revert"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            <div className="space-y-6">
              {SECTIONS.map(section => (
                <Card key={section.title} className="border-stone-200/80">
                  <CardContent className="p-6">
                    <h2 className="font-serif text-xl text-stone-900 mb-3">{section.title}</h2>
                    <div>{section.fields.map(renderField)}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
